<template>
  <div>
    <page-header title="Fechamento da taxa Quero Pago">
      <template #description>
        Valor da nota fiscal da taxa Quero Pago de cada tomador no mês, com as cobranças
        que o compõem. Revise antes de emitir. Gerar de novo substitui o fechamento do mês.
      </template>
    </page-header>

    <div class="row fee-closings__filters">
      <div class="col-md-3">
        <label for="fee-closings-month">Mês</label>
        <input id="fee-closings-month" v-model="month" type="month" class="form-control" @change="loadMonth">
      </div>
      <div class="col-md-3">
        <label for="fee-closings-search">IES ou CNPJ</label>
        <input id="fee-closings-search" v-model="search" type="search" class="form-control fee-closings__search"
               placeholder="Nome da instituição ou CNPJ"
        >
      </div>
      <div class="col-md-6 fee-closings__actions">
        <button v-permissions:fee_closing.generate class="btn btn-primary fee-closings__generate" @click="generate">
          Gerar fechamento
        </button>
        <a v-permissions:fee_closing.review :href="downloadUrl" class="btn btn-default fee-closings__download">
          Baixar planilha
        </a>
        <!-- O lote emite o mês inteiro: com filtro ativo ele some, para ninguém emitir sem a visão geral. -->
        <button v-if="!filterActive" v-permissions:fee_closing.emit class="btn btn-default fee-closings__emit-month"
                @click="showEmitMonthModal = true"
        >
          Emitir notas do mês
        </button>
      </div>
    </div>

    <emit-month-modal v-if="showEmitMonthModal" :month="month" @close="closeEmitMonthModal" />

    <p v-if="closingsMonth && closingsMonth.generated_at" class="fee-closings__generated">
      Gerado por {{ closingsMonth.generated_by }} em {{ closingsMonth.generated_at | formatDateTime }}
    </p>

    <p v-if="closingsMonth && closingsMonth.closings.length === 0" class="fee-closings__empty">
      Nenhum fechamento gerado para este mês.
    </p>

    <p v-if="filterActive && closingsMonth && closingsMonth.closings.length && !visibleClosings.length"
       class="fee-closings__no-match"
    >
      Nenhum tomador encontrado para "{{ search.trim() }}".
    </p>

    <template v-if="institutionClosings.length">
      <table class="table table-striped table-bordered fee-closings__table">
        <thead>
          <tr>
            <th>Tomador (CNPJ)</th>
            <th>Instituição</th>
            <th class="text-right">
              Cobranças
            </th>
            <th class="text-right">
              Valor da nota
            </th>
            <th>Nota</th>
            <th>Ação</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="closing in institutionClosings" :key="closing.id" class="fee-closings__row"
              @click="openClosing(closing)"
          >
            <td>{{ formatCnpj(closing.tomador_document) }}</td>
            <td>{{ closing.institution_names.join(', ') }}</td>
            <td class="text-right">
              {{ closing.items_count }}
            </td>
            <td class="text-right">
              {{ closing.total | formatCurrency }}
            </td>
            <td class="fee-closings__emission">
              <template v-if="closing.emission">
                <!-- O build compacta o espaço entre elementos em linhas separadas: o {{ ' ' }}
                     mantém as partes da nota separadas na tela. -->
                <span class="fee-closings__emission-status">
                  {{ emissionStatusLabel(closing.emission.status) }}
                </span>{{ ' ' }}
                <span v-if="closing.emission.nfse_number" class="fee-closings__emission-nfse">
                  NFS-e {{ closing.emission.nfse_number }}
                </span>{{ ' ' }}
                <span v-if="closing.emission.rejection_message" class="fee-closings__emission-rejection">
                  {{ closing.emission.rejection_message }}
                </span>{{ ' ' }}
                <a v-if="closing.emission.scriba_url" :href="closing.emission.scriba_url" target="_blank"
                   class="fee-closings__emission-scriba-link" @click.stop
                >
                  Ver no Scriba
                </a>
              </template>
              <span v-else-if="!closing.eligibility.eligible" class="fee-closings__ineligible-reason">
                {{ closing.eligibility.reason }}
              </span>
            </td>
            <td>
              <button v-if="closing.can_emit" v-permissions:fee_closing.emit
                      class="btn btn-default btn-sm fee-closings__emit"
                      @click.stop="emitClosing(closing)"
              >
                Emitir
              </button>
            </td>
          </tr>
        </tbody>
        <tfoot>
          <tr>
            <th colspan="2">
              {{ filterActive ? 'Total filtrado' : 'Total que vira nota' }}
            </th>
            <th class="text-right">
              {{ invoiceTotals.itemsCount }}
            </th>
            <th class="text-right">
              {{ invoiceTotals.total | formatCurrency }}
            </th>
            <th colspan="2"></th>
          </tr>
        </tfoot>
      </table>
    </template>

    <template v-if="specialClosings.length">
      <h4>Precisa de decisão (não vira nota)</h4>
      <table class="table table-bordered fee-closings__special">
        <tbody>
          <tr v-for="closing in specialClosings" :key="closing.id" class="fee-closings__row"
              @click="openClosing(closing)"
          >
            <td>{{ specialLabel(closing) }}</td>
            <td>{{ formatCnpj(closing.tomador_document) }}</td>
            <td class="text-right">
              {{ closing.items_count }}
            </td>
            <td class="text-right">
              {{ closing.total | formatCurrency }}
            </td>
          </tr>
        </tbody>
      </table>
    </template>
  </div>
</template>

<script>
import FeeClosingsService from 'services/FeeClosingsService';
import Permissions from 'mixins/Permissions';
import EmitMonthModal from './EmitMonthModal.vue';
import { SPECIAL_TOMADORES, EMISSION_STATUSES, formatCnpj } from './labels';

function previousMonth() {
  const today = new Date();
  const date = new Date(today.getFullYear(), today.getMonth() - 1, 1);

  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
}

// Sem acento e em minúsculas, para "sao vicente" achar "São Vicente".
function normalize(text) {
  return text.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
}

export default {
  mixins: [Permissions],
  components: {
    EmitMonthModal,
  },
  data() {
    return {
      month: this.$route.query.month || previousMonth(),
      closingsMonth: null,
      showEmitMonthModal: false,
      search: '',
    };
  },
  computed: {
    // Link direto: o navegador baixa o .xlsx com a sessão do Admin.
    downloadUrl() {
      return `${this.$serverHost}/admin/fee-closings/export.xlsx?month=${this.month}`;
    },
    filterActive() {
      return this.search.trim() !== '';
    },
    // O filtro roda sobre o mês já carregado: o mês inteiro cabe numa tela (dezenas de tomadores).
    visibleClosings() {
      const closings = this.closingsMonth?.closings || [];
      if (!this.filterActive) return closings;

      const term = normalize(this.search.trim());
      const digits = this.search.replace(/\D/g, '');
      return closings.filter(closing => closing.institution_names.some(name => normalize(name).includes(term))
        || (digits !== '' && (closing.tomador_document || '').includes(digits)));
    },
    institutionClosings() {
      return this.visibleClosings.filter(closing => closing.tomador_kind === 'institution');
    },
    specialClosings() {
      return this.visibleClosings.filter(closing => closing.tomador_kind !== 'institution');
    },
    invoiceTotals() {
      if (!this.filterActive) {
        return { itemsCount: this.closingsMonth.invoice_items_count, total: this.closingsMonth.invoice_total };
      }

      const cents = this.institutionClosings.reduce((sum, closing) => sum + Math.round(Number(closing.total) * 100), 0);
      return {
        itemsCount: this.institutionClosings.reduce((sum, closing) => sum + closing.items_count, 0),
        total: cents / 100,
      };
    },
  },
  mounted() {
    this.loadMonth();
  },
  methods: {
    formatCnpj,

    specialLabel(closing) {
      return SPECIAL_TOMADORES[closing.tomador_kind];
    },

    emissionStatusLabel(status) {
      return EMISSION_STATUSES[status];
    },

    emitClosing(closing) {
      this.request(FeeClosingsService.emitClosing(closing.id).then(() => FeeClosingsService.getMonth(this.month)),
        'Erro ao emitir a nota.');
    },

    closeEmitMonthModal() {
      this.showEmitMonthModal = false;
      this.loadMonth();
    },

    loadMonth() {
      this.request(FeeClosingsService.getMonth(this.month), 'Erro ao carregar o fechamento.');
    },

    generate() {
      this.request(FeeClosingsService.generate(this.month).then(() => FeeClosingsService.getMonth(this.month)),
        'Erro ao gerar o fechamento.')
        .then((succeeded) => {
          if (succeeded) this.$alert.notifySuccess('Fechamento gerado.');
        });
    },

    // A resposta de um mês que já não está selecionado é descartada, para a tabela
    // nunca mostrar um mês diferente do que está no seletor e no link da planilha.
    request(promise, errorMessage) {
      const requestedMonth = this.month;
      this.$loader.show();

      return promise
        .then((closingsMonth) => {
          if (requestedMonth === this.month) this.closingsMonth = closingsMonth;
          this.$loader.hide();
          return true;
        })
        .catch((error) => {
          this.$loader.hide();
          this.$alert.notifyAlert(error.response?.data?.message || errorMessage);
          return false;
        });
    },

    openClosing(closing) {
      this.$router.push({ name: 'fee-closing-details', params: { id: closing.id } });
    },
  },
};
</script>

<style scoped>
  .fee-closings__actions {
    padding-top: 25px;
  }

  .fee-closings__download,
  .fee-closings__emit-month {
    margin-left: 10px;
  }

  .fee-closings__filters {
    margin-bottom: 20px;
  }

  .fee-closings__row {
    cursor: pointer;
  }
</style>
